import { deepLinkConfig } from '../config/deepLinkConfig';

/**
 * Detect the user's platform based on user agent
 * @returns {'ios' | 'android' | 'desktop'}
 */
export const detectPlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'ios';
    }

    // Android detection
    if (/android/i.test(userAgent)) {
        return 'android';
    }

    // Default to desktop
    return 'desktop';
};

/**
 * Check if the current device is mobile
 * @returns {boolean}
 */
export const isMobileDevice = () => {
    const platform = detectPlatform();
    return platform === 'ios' || platform === 'android';
};

/**
 * Parse referral/deeplink data from URL pathname and search params
 * Supports multiple formats:
 * - /ref/JAMA-5M4VD8BG → { type: 'referral', code: 'JAMA-5M4VD8BG' }
 * - /ref/3?ref_code=JAMA-5M4VD8BG → { type: 'referral', userId: '3', code: 'JAMA-5M4VD8BG' }
 * - /@juanperez → { type: 'profile', username: 'juanperez' }
 * - /food/123 → { type: 'food', restaurantId: '123' }
 *
 * @param {string} pathname - URL pathname
 * @param {string} search - URL search parameters
 * @returns {Object} Parsed link data
 */
export const parseReferralData = (pathname, search) => {
    const searchParams = new URLSearchParams(search);
    const params = {};

    // Extract all query parameters
    for (let [key, value] of searchParams.entries()) {
        params[key] = value;
    }

    // Parse pathname
    const pathParts = pathname.split('/').filter(Boolean);

    // Handle different URL formats
    if (pathname.startsWith('/ref/')) {
        const pathValue = pathParts[1];
        // Check if path value is numeric (userId) or alphanumeric (referral code)
        const isNumeric = /^\d+$/.test(pathValue);

        // If numeric, it's a userId, and code should come from query params
        // If alphanumeric, it's the code directly
        const userId = isNumeric ? pathValue : undefined;
        const code = isNumeric ? params.ref_code : pathValue;

        return {
            type: 'referral',
            userId,
            code,
            params,
        };
    }

    if (pathname.startsWith('/referral')) {
        return {
            type: 'referral',
            code: params.ref_code || params.code,
            params,
        };
    }

    if (pathname.startsWith('/@')) {
        return {
            type: 'profile',
            username: pathParts[0]?.replace('@', ''),
            params,
        };
    }

    if (pathname.startsWith('/food/')) {
        return {
            type: 'food',
            restaurantId: pathParts[1],
            params,
        };
    }

    if (pathname.startsWith('/c/')) {
        return {
            type: 'coupon',
            code: pathParts[1],
            params,
        };
    }

    if (pathname.startsWith('/p/')) {
        return {
            type: 'product',
            productId: pathParts[1],
            restaurantId: params.restaurant_id,
            params,
        };
    }

    if (pathname.startsWith('/home')) {
        return {
            type: 'home',
            tab: params.tab || 'comida',
            params,
        };
    }

    if (pathname.startsWith('/app')) {
        return {
            type: 'app',
            params,
        };
    }

    // Default fallback
    return {
        type: 'unknown',
        pathname,
        params,
    };
};

/**
 * Build custom scheme URL for direct app launching
 * Format: jama://ref/{id}?ref_code=XXX
 * Examples:
 * - jama://ref/3?ref_code=JAMA-5M4VD8BG
 * - jama://profile/juanperez
 * - jama://food/123
 *
 * @param {Object} linkData - Parsed link data from parseReferralData
 * @returns {string} Custom scheme URL
 */
export const buildCustomSchemeUrl = (linkData) => {
    const { type, code, username, restaurantId, userId } = linkData;

    let path = '';
    let queryParams = new URLSearchParams();

    // Build path based on type
    switch (type) {
        case 'referral':
            // New format: jama://ref/{code}
            // Old format: jama://ref/{userId}?ref_code={code}
            if (userId && code) {
                // Old format with userId + code
                path = `ref/${userId}`;
                queryParams.append('ref_code', code);
            } else if (code) {
                // New format: just the code directly in path
                path = `ref/${code}`;
            } else if (userId) {
                // Only userId (edge case)
                path = `ref/${userId}`;
            } else {
                path = 'ref';
            }
            break;

        case 'profile':
            path = `profile/${username}`;
            break;

        case 'food':
            path = `food/${restaurantId}`;
            break;

        case 'coupon':
            path = code ? `c/${code}` : 'c';
            break;

        case 'product':
            path = `product`;
            if (linkData.productId) queryParams.append('id', linkData.productId);
            if (linkData.restaurantId) queryParams.append('restaurant_id', linkData.restaurantId);
            break;

        case 'home':
            path = 'home';
            if (linkData.tab && linkData.tab !== 'comida') queryParams.append('tab', linkData.tab);
            break;

        case 'app':
            path = '';
            break;

        default:
            path = '';
    }

    // Build final URL
    const baseUrl = `${deepLinkConfig.customScheme}${path}`;
    const queryString = queryParams.toString();

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Get App Store or Google Play URL with referral data embedded
 * @param {'ios' | 'android' | 'desktop'} platform - Platform
 * @param {Object} linkData - Parsed link data
 * @returns {string} Store URL
 */
export const getStoreUrl = (platform, linkData) => {
    const { code, params = {} } = linkData;

    // Build campaign parameters
    const campaignParams = new URLSearchParams();

    // Add referral code if exists
    if (code) {
        campaignParams.append('ref_code', code);
    }

    // Add UTM parameters
    campaignParams.append('utm_source', params.utm_source || 'web');
    campaignParams.append('utm_medium', params.utm_medium || 'deeplink');
    campaignParams.append('utm_campaign', params.utm_campaign || (code ? `ref_${code}` : 'app_download'));

    // Add any other query params
    Object.entries(params).forEach(([key, value]) => {
        if (!key.startsWith('utm_') && key !== 'ref_code') {
            campaignParams.append(key, value);
        }
    });

    const campaignString = campaignParams.toString();

    if (platform === 'ios') {
        // iOS App Store with campaign tracking
        const baseUrl = deepLinkConfig.storeLinks.ios;
        // Add campaign token for attribution
        return campaignString ? `${baseUrl}?ct=${code || 'web'}&${campaignString}` : baseUrl;
    }

    if (platform === 'android') {
        // Google Play with referrer parameter
        const baseUrl = deepLinkConfig.storeLinks.android;
        const referrer = encodeURIComponent(campaignString);
        return `${baseUrl}&referrer=${referrer}`;
    }

    // Fallback to iOS store for desktop
    return deepLinkConfig.storeLinks.ios;
};

/**
 * Detect if browser is an in-app browser (Facebook, Instagram, etc.)
 * These browsers often don't support Universal Links properly
 * @returns {boolean}
 */
export const isInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Check for common in-app browser user agents
    const inAppBrowsers = [
        'FBAN', // Facebook App
        'FBAV', // Facebook App
        'Instagram', // Instagram
        'Line', // Line App
        'WhatsApp', // WhatsApp (though it handles links better)
        'Twitter', // Twitter/X App
    ];

    return inAppBrowsers.some(browser => userAgent.includes(browser));
};

/**
 * Generate a universal link (HTTPS URL that can be handled by app or web)
 * @param {Object} linkData - Parsed link data
 * @returns {string} Universal link URL
 */
export const generateUniversalLink = (linkData) => {
    const { type, code, username, restaurantId, userId } = linkData;

    const domain = window.location.origin;

    switch (type) {
        case 'referral':
            if (code) {
                return `${domain}/ref/${code}`;
            } else if (userId) {
                return `${domain}/ref/${userId}`;
            }
            return `${domain}/referral`;

        case 'profile':
            return `${domain}/@${username}`;

        case 'food':
            return `${domain}/food/${restaurantId}`;

        case 'coupon':
            return code ? `${domain}/c/${code}` : `${domain}/app`;

        case 'app':
            return `${domain}/app`;

        default:
            return domain;
    }
};
